import { Box, Stack, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { Fields, Form } from 'common/form';
import { TextBlock } from 'common/ui/TextBlock';
import { useEffect, useState } from 'react';
import { useDrawer } from 'services/drawer/hooks';
import { JobLocation } from 'services/integration/types';
import Yup from 'utils/validation';
import { convertHtmlToFormattedText } from '../utils/convertHtmlToFormattedText';
import { Refresh } from '@mui/icons-material';
import { useConfirmationModal } from 'services/modals/hooks';
import { ToastType, useToast } from 'services/toast';

const validationSchema = Yup.object({
  title: Yup.string().required('Enter a job title'),
  location: Yup.string().required('Enter a location'),
  description: Yup.string().required('Enter a job description')
});

const resetButtonSx: SxProps = { px: 0 };

export type JobDescriptionFormData = Yup.InferType<typeof validationSchema>;

const fields: Fields<JobDescriptionFormData> = {
  title: {
    type: 'text',
    label: 'Job title'
  },
  location: {
    type: 'text',
    label: 'Location'
  },
  description: {
    type: 'textarea',
    label: 'Job description',
    subLabel: 'Used by Popp to generate requirements.',
    rows: 10
  }
};

// TODO: Add DrawerProps typing on openDrawer
type Props = {
  jobDescription: string;
  title: string;
  locations: JobLocation[];
  onChoose: (jobInput: JobDescriptionFormData) => void;
};

export const JobDescriptionHtmlDrawer = ({
  jobDescription,
  title,
  locations,
  onChoose
}: Props) => {
  const { showModal } = useConfirmationModal();
  const { showToast } = useToast();
  const { closeDrawer } = useDrawer();
  const [renderedHtml, setRenderedHtml] = useState(false);
  const [originalDescription, setOriginalDescription] = useState('');
  const [formData, setFormData] = useState<JobDescriptionFormData>({
    title,
    location: locations.map((location) => location.name).join(', '),
    description: ''
  });

  useEffect(() => {
    const formattedDescription = convertHtmlToFormattedText(jobDescription);
    setOriginalDescription(formattedDescription);
    setFormData((prev) => ({ ...prev, description: formattedDescription }));
    setRenderedHtml(true);
  }, [jobDescription]);

  return (
    <Stack width="479px" gap="20px" pb="20px">
      <TextBlock variant="drawer" title={formData.title} />
      {renderedHtml && (
        <Form
          defaultValues={formData}
          schema={validationSchema}
          fields={fields}
          onSubmit={(data) => {
            onChoose(data);
            closeDrawer();
          }}
          children={
            <Box>
              <Button
                sx={resetButtonSx}
                variant="tertiary"
                onClick={() => {
                  showModal({
                    title: 'Revert to original?',
                    message:
                      'You will lose any changes you or others have made to this job description.',
                    onConfirm: () => {
                      setFormData({
                        title,
                        location: locations
                          .map((location) => location.name)
                          .join(', '),
                        description: originalDescription
                      });
                      showToast({
                        message: 'Job description reset',
                        type: ToastType.Success
                      });
                    },
                    confirmationText: 'Reset'
                  });
                }}
              >
                <Refresh /> Reset all changes
              </Button>
            </Box>
          }
          onCancel={() => closeDrawer()}
          submitButtonProps={{
            label: 'Save and continue'
          }}
        />
      )}
    </Stack>
  );
};
